// Delivery GPS on the order detail page. Read-only marker by default; when the
// order is in an editable status and the user has order-save permission, the
// marker can be placed by clicking the map or dragging, then saved via
// PUT /ord/order/:id/delivery-location.

import { Badge, Box, Button, Group, Paper, Stack, Text } from "@mantine/core"
import { DotPill } from "../delivery/atoms"
import { MapPinIcon, PencilSimpleIcon, FloppyDiskIcon, XIcon } from "@phosphor-icons/react"
import { showNotification } from "@dehub/theme"
import { MarkerF } from "@react-google-maps/api"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { OrderApi } from "../../apis"
import { MapCanvas, UB_CENTER } from "../ui/google-map"

// Ulaanbaatar centre — map view fallback when the order has no coordinates yet.
const UB: [number, number] = [UB_CENTER.lat, UB_CENTER.lng]

type LatLng = [number, number]

// Delivery GPS is editable while the order is still active (mirrors the
// de-order-service set-delivery-location guard). A delivered order stays
// AUTHORIZED since 2026-11-04, so the delivered date locks it.
export const DELIVERY_LOCATION_EDITABLE_STATUSES = [
  "DRAFT",
  "REGISTERED",
  "REVIEWED",
  "APPROVED",
  "AUTHORIZED",
]

export function isDeliveryLocationEditable(status?: string | null, deliveredDate?: string | null): boolean {
  return !!status && DELIVERY_LOCATION_EDITABLE_STATUSES.includes(status) && !deliveredDate
}

type Props = {
  orderId: string
  latitude?: number | null
  longitude?: number | null
  // Seed for a first-time pick (e.g. the receiver branch coordinates).
  fallbackLat?: number | null
  fallbackLng?: number | null
  canEdit?: boolean
  onSaved?: () => void
  height?: number
  // Compact one-row look while no point is set (buyer order detail, canvas OrdB_OrderDetail).
  compactUnset?: boolean
  // Supplier detail canvases (Ord_SalesDetail / Ord_PurchaseDetail): neutral default-variant
  // buttons, dark pin, card fills its grid cell. Off by default so the buyer look is unchanged.
  quiet?: boolean
  // Ord_SalesDetail: while no point is set the body carries badge, sentence and button
  // (the header shows the title only).
  unsetInBody?: boolean
}

export function DeliveryLocationMap({
  orderId,
  latitude,
  longitude,
  fallbackLat,
  fallbackLng,
  canEdit = false,
  onSaved,
  height = 320,
  compactUnset = false,
  quiet = false,
  unsetInBody = false,
}: Props) {
  const { t } = useTranslation(["order", "common"])

  const saved: LatLng | null =
    typeof latitude === "number" && typeof longitude === "number" ? [latitude, longitude] : null
  const fallback: LatLng | null = useMemo(
    () =>
      typeof fallbackLat === "number" && typeof fallbackLng === "number"
        ? [fallbackLat, fallbackLng]
        : null,
    [fallbackLat, fallbackLng],
  )

  const [editing, setEditing] = useState(false)
  const [pos, setPos] = useState<LatLng | null>(saved)
  const [saving, setSaving] = useState(false)

  const center: LatLng = useMemo(() => pos ?? fallback ?? UB, [pos, fallback])

  const handleEdit = () => {
    setPos(saved ?? fallback)
    setEditing(true)
  }

  const handleCancel = () => {
    setPos(saved)
    setEditing(false)
  }

  const handleSave = async () => {
    if (!pos) return
    setSaving(true)
    try {
      await OrderApi.setDeliveryLocation(orderId, { latitude: pos[0], longitude: pos[1] })
      showNotification({
        color  : "green",
        title  : t("common:messages.success", { defaultValue: "Амжилттай" }),
        message: t("order:deliveryLocation.saved", { defaultValue: "Хүргэлтийн байршил хадгалагдлаа" }),
      })
      setEditing(false)
      onSaved?.()
    } catch {
      // http-handler already surfaces the error toast — don't double-notify.
    } finally {
      setSaving(false)
    }
  }

  if (compactUnset && !saved && !editing) {
    return (
      <Paper withBorder radius="md" px="md" py="sm">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Box miw={0}>
            <Text size="sm" fw={600}>
              {t("order:deliveryLocation.title", { defaultValue: "Хүргэлтийн байршил" })}
            </Text>
            <Text size="sm" c="dimmed">
              {t("order:deliveryLocation.unsetHint")}
            </Text>
          </Box>
          <Group gap="sm" wrap="nowrap">
            <Badge variant="light" color="gray" radius="sm">
              {t("order:deliveryLocation.notSet", { defaultValue: "Тохируулаагүй" })}
            </Badge>
            {canEdit && (
              <Button size="sm" variant="default" leftSection={<MapPinIcon size={14} />} onClick={handleEdit}>
                {t("order:deliveryLocation.set", { defaultValue: "Байршил тохируулах" })}
              </Button>
            )}
          </Group>
        </Group>
      </Paper>
    )
  }

  const bodyUnset = unsetInBody && !saved && !editing
  const setLabel = t("order:deliveryLocation.set", { defaultValue: "Байршил тохируулах" })
  const emptyText = t("order:deliveryLocation.empty", {
    defaultValue: "Энэ захиалгад хүргэлтийн байршил тохируулаагүй байна.",
  })

  return (
    <Paper withBorder radius={quiet ? "lg" : "md"} p={0} h={quiet ? "100%" : undefined} style={{ overflow: "hidden" }}>
      <Group
        justify="space-between"
        align="center"
        px={quiet ? "lg" : "md"}
        py={quiet ? "md" : "sm"}
        wrap={quiet ? "nowrap" : undefined}
        style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}
      >
        <Group gap={8} wrap={quiet ? "nowrap" : undefined}>
          {!unsetInBody && (
            <MapPinIcon size={16} color={quiet ? "var(--mantine-color-dark-7)" : "var(--mantine-color-brand-6)"} />
          )}
          {/* One line as in the canvas: the title and pill never wrap or truncate. */}
          <Text fz={quiet ? 16 : 14} fw={quiet ? 700 : 600} style={{ whiteSpace: "nowrap" }}>
            {t("order:deliveryLocation.title", { defaultValue: "Хүргэлтийн байршил" })}
          </Text>
          {!saved && !editing && !bodyUnset && (
            <DotPill tone="grey">{t("order:deliveryLocation.notSet", { defaultValue: "Тохируулаагүй" })}</DotPill>
          )}
        </Group>

        {canEdit && !bodyUnset &&
          (editing ? (
            <Group gap="xs">
              <Button
                size="sm"
                variant="default"
                leftSection={<XIcon size={14} />}
                onClick={handleCancel}
                disabled={saving}
              >
                {t("common:actions.cancel", { defaultValue: "Болих" })}
              </Button>
              <Button
                size="sm"
                color="brand"
                leftSection={<FloppyDiskIcon size={14} />}
                onClick={handleSave}
                loading={saving}
                disabled={!pos}
              >
                {t("common:actions.save", { defaultValue: "Хадгалах" })}
              </Button>
            </Group>
          ) : (
            <Button
              size={quiet ? "sm" : "xs"}
              style={{ flexShrink: 0 }}
              variant={quiet ? "default" : "light"}
              color={quiet ? undefined : "brand"}
              leftSection={quiet ? <MapPinIcon size={14} /> : <PencilSimpleIcon size={14} />}
              onClick={handleEdit}
            >
              {saved
                ? t("order:deliveryLocation.edit", { defaultValue: "Байршил засах" })
                : setLabel}
            </Button>
          ))}
      </Group>

      {bodyUnset ? (
        <Stack gap="md" align="flex-start" p="lg">
          <DotPill tone="grey">{t("order:deliveryLocation.notSet", { defaultValue: "Тохируулаагүй" })}</DotPill>
          <Text size="sm" c="dark.6">{emptyText}</Text>
          {canEdit && (
            <Button variant="default" leftSection={<MapPinIcon size={14} />} onClick={handleEdit}>
              {setLabel}
            </Button>
          )}
        </Stack>
      ) : !saved && !editing ? (
        <Box p="lg">
          <Text size="sm" c={quiet ? "dark.6" : "dimmed"} ta="center">
            {emptyText}
          </Text>
        </Box>
      ) : (
        <Box pos="relative" style={{ height }}>
          <MapCanvas
            height="100%"
            center={{ lat: center[0], lng: center[1] }}
            zoom={pos ? 15 : 12}
            onClick={editing ? (p) => setPos([p.lat, p.lng]) : undefined}
          >
            {pos && (
              <MarkerF
                position={{ lat: pos[0], lng: pos[1] }}
                draggable={editing}
                onDragEnd={(e) => e.latLng && setPos([e.latLng.lat(), e.latLng.lng()])}
              />
            )}
          </MapCanvas>

          {editing && (
            <Paper
              radius="md"
              shadow="sm"
              p="xs"
              pos="absolute"
              top={12}
              left={12}
              right={12}
              bg="var(--mantine-color-body)"
              style={{ zIndex: 500 }}
            >
              <Text size="sm" c="dimmed" ta="center">
                {t("order:deliveryLocation.hint", {
                  defaultValue: "Газрын зураг дээр дарж эсвэл тэмдэглэгээг чирж байршлыг сонгоно уу",
                })}
              </Text>
            </Paper>
          )}

          {pos && (
            <Paper
              radius="md"
              shadow="sm"
              p="xs"
              pos="absolute"
              bottom={12}
              left={12}
              bg="var(--mantine-color-body)"
              style={{ zIndex: 500 }}
            >
              <Group gap="lg">
                <Box>
                  <Text size="10px" c="dimmed" tt="uppercase">
                    Lat
                  </Text>
                  <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                    {pos[0].toFixed(6)}
                  </Text>
                </Box>
                <Box>
                  <Text size="10px" c="dimmed" tt="uppercase">
                    Lng
                  </Text>
                  <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                    {pos[1].toFixed(6)}
                  </Text>
                </Box>
              </Group>
            </Paper>
          )}
        </Box>
      )}
    </Paper>
  )
}
